import { CheckCircle2, ClipboardList, CalendarClock, Truck, PackageCheck, ReceiptText } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useOrderTracking, type TrackingStage } from '@/hooks/useOrderTracking';

/**
 * OrderTracker — branded status stepper for a single order's live delivery
 * progress. Renders the normalized stage ladder
 *   submitted → scheduled → in_progress → completed → invoiced
 * with the current step highlighted in navy, plus the scheduled / delivered
 * date when SF has one.
 *
 * Data comes from useOrderTracking (the customer-scoped order-tracking
 * function). On an SF outage the payload is `degraded` and we show a calm
 * "Status updates as your delivery is scheduled" note instead of an error.
 */

interface StepDef {
  key: TrackingStage;
  label: string;
  Icon: typeof ClipboardList;
}

const STEPS: StepDef[] = [
  { key: 'submitted', label: 'Submitted', Icon: ClipboardList },
  { key: 'scheduled', label: 'Scheduled', Icon: CalendarClock },
  { key: 'in_progress', label: 'On the way', Icon: Truck },
  { key: 'completed', label: 'Delivered', Icon: PackageCheck },
  { key: 'invoiced', label: 'Invoiced', Icon: ReceiptText },
];

const STAGE_INDEX: Record<TrackingStage, number> = {
  submitted: 0,
  scheduled: 1,
  in_progress: 2,
  completed: 3,
  invoiced: 4,
};

function fmtDate(value: string | null): string | null {
  if (!value) return null;
  // Accept YYYY-MM-DD (date-only, no TZ shift) and full ISO timestamps.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  try {
    const d = dateOnly ? new Date(`${value}T12:00:00`) : new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

export function OrderTracker({ orderId }: { orderId: string }) {
  const { data: tracking, isLoading } = useOrderTracking(orderId);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-brix-border bg-brix-surface shadow-card p-5 animate-pulse">
        <div className="h-3 w-28 rounded bg-brix-border/70 mb-5" />
        <div className="flex justify-between gap-2">
          {STEPS.map((s) => (
            <div key={s.key} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-brix-border/60" />
              <div className="h-2 w-12 rounded bg-brix-border/50" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!tracking) return null;

  const currentIndex = STAGE_INDEX[tracking.stage];
  const scheduled = fmtDate(tracking.scheduledDate);
  const delivered = fmtDate(tracking.completedDate);

  return (
    <div className="rounded-2xl border border-brix-border bg-brix-surface shadow-card p-5">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-base font-semibold text-brix-ink">
          Delivery status
        </h2>
        {tracking.status && !tracking.degraded && (
          <span className="text-[11px] font-semibold tracking-tight px-2.5 py-0.5 rounded-full bg-brix-primary-tint text-brix-primary border border-brix-primary/30">
            {tracking.status}
          </span>
        )}
      </div>

      {/* Stepper */}
      <div className="flex items-start justify-between gap-1">
        {STEPS.map((step, i) => {
          const isDone = i < currentIndex;
          const isCurrent = i === currentIndex;
          const isActive = i <= currentIndex;
          const Icon = isDone ? CheckCircle2 : step.Icon;
          return (
            <div key={step.key} className="flex-1 flex flex-col items-center text-center min-w-0">
              <div className="relative w-full flex items-center justify-center">
                {/* connector to previous step */}
                {i > 0 && (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute right-1/2 top-[18px] h-0.5 w-full -translate-y-1/2',
                      i <= currentIndex ? 'bg-brix-primary' : 'bg-brix-border',
                    )}
                  />
                )}
                <span
                  className={cn(
                    'relative z-10 flex items-center justify-center w-9 h-9 rounded-full border-2 transition-colors',
                    isCurrent && 'bg-brix-primary border-brix-primary text-white shadow-card',
                    isDone && 'bg-brix-primary border-brix-primary text-white',
                    !isActive && 'bg-brix-surface border-brix-border text-brix-muted',
                  )}
                >
                  <Icon size={16} />
                </span>
              </div>
              <span
                className={cn(
                  'mt-2 text-[11px] font-semibold tracking-tight leading-tight',
                  isActive ? 'text-brix-ink' : 'text-brix-muted',
                )}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Dates + degraded note */}
      <div className="mt-5 pt-4 border-t border-brix-border space-y-1.5">
        {delivered ? (
          <p className="text-sm text-brix-ink flex items-center gap-1.5">
            <PackageCheck size={14} className="text-brix-success shrink-0" />
            Delivered <span className="font-medium">{delivered}</span>
          </p>
        ) : scheduled ? (
          <p className="text-sm text-brix-ink flex items-center gap-1.5">
            <CalendarClock size={14} className="text-brix-primary shrink-0" />
            Scheduled for <span className="font-medium">{scheduled}</span>
          </p>
        ) : (
          <p className="text-sm text-brix-muted">
            Status updates as your delivery is scheduled.
          </p>
        )}
        {tracking.degraded && (scheduled || delivered) && (
          <p className="text-[11px] text-brix-muted">
            Status updates as your delivery is scheduled.
          </p>
        )}
      </div>
    </div>
  );
}
